import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Portal,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AccountTree as AccountTreeIcon,
  CalendarMonth as CalendarIcon,
  CloseFullscreen as CloseFullscreenIcon,
  OpenInFull as OpenInFullIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { expertApi } from '../../services/api';
import { AISystem } from '../../types';
import { AgentSchedulePanel } from './AgentSchedulePanel';
import AISystemCanvas from './AISystemCanvas';
import { SchedulePanel } from './SchedulePanel';
import { ScheduleExecutionsScreen } from './ScheduleExecutionsScreen';

interface AIBuilderInsightsPanelProps {
  system: AISystem;
  sessionData?: any;
  isHistoricalSession?: boolean;
  selectedMessageId?: string | null;
  nodes?: Node[];
  edges?: Edge[];
  onNodesChange?: any;
  onEdgesChange?: any;
  onConnect?: any;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onAutoLayout?: () => void;
  expertsData?: any;
  addExpertToCanvas?: (expert: any) => void;
  setExpertFormOpen?: (open: boolean) => void;
  selectedNode?: Node | null;
  setSelectedNode?: (node: Node | null) => void;
  setNodes?: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<Edge[]>>;
  refetchExperts?: () => void;
  validationIssues?: any[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  onStopCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  isStoppingCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, any>;
  onViewExpertRun?: (expertId: number) => void;
  nodeTypes?: any;
  onExpertUpdate?: (expert: any | null) => void;
  onExitSnapshot?: () => void;
  isMobile?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ height: '100%', overflow: 'auto', minWidth: 0, width: '100%' }}
    >
      {value === index && (
        <Box sx={{ pt: { xs: 0.5, sm: 1 }, px: 0, pb: 0, minWidth: 0, width: '100%', height: '100%' }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const CanvasResizeFitView: React.FC<{ isFullscreen: boolean; nodeCount: number }> = ({ isFullscreen, nodeCount }) => {
  const { fitView } = useReactFlow();

  React.useEffect(() => {
    if (nodeCount === 0) return;

    const timer = window.setTimeout(() => {
      fitView({ padding: 0.25, maxZoom: 1 });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [fitView, isFullscreen, nodeCount]);

  return null;
};

const AIBuilderInsightsPanel: React.FC<AIBuilderInsightsPanelProps> = ({
  system,
  sessionData,
  selectedMessageId = null,
  nodes: canvasNodes = [],
  edges: canvasEdges = [],
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData: canvasExpertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  setSelectedNode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues = [],
  onSaveCanvas,
  isSavingCanvas = false,
  onRunCanvas,
  onStopCanvas,
  isRunningCanvas = false,
  isStoppingCanvas = false,
  canRunCanvas,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  onExitSnapshot,
  isMobile = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [forceLiveView, setForceLiveView] = useState(false);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);

  const hasValidSnapshot =
    !!sessionData?.system_snapshot &&
    Array.isArray(sessionData.system_snapshot.experts) &&
    sessionData.system_snapshot.experts.length > 0;

  const shouldUseSnapshot = selectedMessageId !== null && hasValidSnapshot && !forceLiveView;

  React.useEffect(() => {
    setForceLiveView(false);
  }, [sessionData?.id, selectedMessageId]);

  const hasInteractiveCanvasProps = Boolean(
    onNodesChange &&
      onEdgesChange &&
      onConnect &&
      onNodeClick &&
      onAutoLayout &&
      addExpertToCanvas &&
      setExpertFormOpen &&
      setSelectedNode &&
      setNodes &&
      setEdges &&
      refetchExperts
  );

  const canUseInteractiveCanvas = !shouldUseSnapshot && hasInteractiveCanvasProps;

  const { data: expertsData } = useQuery({
    queryKey: ['experts', system.id, 'preview-fallback'],
    queryFn: () => expertApi.list({ ai_system: system.id }),
    enabled: !canUseInteractiveCanvas && !shouldUseSnapshot,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const snapshotSystem = shouldUseSnapshot ? sessionData.system_snapshot.system : null;
  const snapshotExperts = shouldUseSnapshot ? sessionData.system_snapshot.experts : null;
  const experts = shouldUseSnapshot ? snapshotExperts : (expertsData?.results || []);
  const displaySystem = shouldUseSnapshot ? snapshotSystem : system;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  React.useEffect(() => {
    if (!isCanvasFullscreen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCanvasFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCanvasFullscreen]);

  const buildFlowData = useCallback(() => {
    const nodes: Node[] = experts.map((expert: any, index: number) => ({
      id: expert.id.toString(),
      type: 'default',
      position: {
        x: displaySystem?.config?.expert_positions?.[expert.id]?.x ?? expert.position_x ?? (index % 3) * 280,
        y: displaySystem?.config?.expert_positions?.[expert.id]?.y ?? expert.position_y ?? Math.floor(index / 3) * 180,
      },
      data: {
        label: (
          <Box sx={{ textAlign: 'center', p: 1, maxWidth: 240 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.2,
                minHeight: '2.4em',
              }}
            >
              {expert.name}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
              {expert.expert_type}
            </Typography>
            {expert.model?.name && (
              <Typography
                variant="caption"
                display="block"
                sx={{
                  fontSize: '0.65rem',
                  color: 'info.main',
                  mt: 0.25,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {expert.model.name}
              </Typography>
            )}
          </Box>
        ),
        isDark,
      },
      style: {
        background: expert.status === 'ACTIVE' ? (isDark ? '#1e3a5f' : '#e3f2fd') : (isDark ? '#2d2d2d' : '#f5f5f5'),
        border: `2px solid ${expert.status === 'ACTIVE' ? '#2196f3' : '#9e9e9e'}`,
        borderRadius: 12,
        padding: 4,
        width: 240,
        minHeight: 100,
        color: isDark ? '#fff' : 'inherit',
      },
    }));

    const edges: Edge[] = [];

    experts.forEach((expert: any) => {
      if (expert.connections && Array.isArray(expert.connections)) {
        expert.connections.forEach((targetId: number) => {
          edges.push({
            id: `e-${expert.id}-${targetId}`,
            source: expert.id.toString(),
            target: targetId.toString(),
            type: 'smoothstep',
            animated: true,
            label: 'depends on',
            style: { stroke: '#2196f3', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#2196f3',
              width: 20,
              height: 20,
            },
          });
        });
      }
    });

    const systemConnections = displaySystem?.config?.expert_connections;
    if (systemConnections && Array.isArray(systemConnections)) {
      systemConnections.forEach((connection: any) => {
        const edgeId = `e-${connection.source}-${connection.target}`;
        if (!edges.find((edge) => edge.id === edgeId)) {
          edges.push({
            id: edgeId,
            source: connection.source.toString(),
            target: connection.target.toString(),
            type: 'smoothstep',
            animated: true,
            label: 'depends on',
            style: { stroke: '#2196f3', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#2196f3',
              width: 20,
              height: 20,
            },
          });
        }
      });
    }

    return { nodes, edges };
  }, [experts, displaySystem, isDark]);

  const { nodes, edges } = buildFlowData();
  const fullscreenToggleLabel = isCanvasFullscreen ? 'Exit fullscreen canvas' : 'Fullscreen canvas';
  const fullscreenControl = (
    <Tooltip title={fullscreenToggleLabel}>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        aria-label={fullscreenToggleLabel}
        onClick={() => setIsCanvasFullscreen((current) => !current)}
        startIcon={isCanvasFullscreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
        sx={{
          minHeight: 30,
          whiteSpace: 'nowrap',
          px: 1.25,
        }}
      >
        {isCanvasFullscreen ? 'Exit' : 'Fullscreen'}
      </Button>
    </Tooltip>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        margin: 0,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradient 15s ease infinite',
        '@keyframes gradient': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: { xs: 40, sm: 48 },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              minWidth: { xs: 80, sm: 120 },
              minHeight: { xs: 40, sm: 48 },
              py: { xs: 0.5, sm: 1 },
              textTransform: 'none',
              borderBottom: '3px solid transparent',
              '&.Mui-selected': {
                borderBottom: '3px solid',
                borderBottomColor: 'primary.main',
              },
            },
          }}
        >
          <Tab icon={<AccountTreeIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label="Workflow" />
          <Tab icon={<CalendarIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label={isMobile ? 'System Schedules' : 'AI System Schedules'} />
          <Tab icon={<ScheduleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label="Agent Schedules" />
          <Tab icon={<CalendarIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label={isMobile ? 'Runs' : 'Agent Schedule Executions'} />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: 'background.paper', minWidth: 0, width: '100%' }}>
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: { xs: 1, sm: 2 } }}>
            {shouldUseSnapshot && (
              <Alert
                severity="info"
                icon={<AccessTimeIcon />}
                sx={{
                  mb: 2,
                  py: 0.5,
                  '& .MuiAlert-message': { py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' },
                }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => {
                      setForceLiveView(true);
                      onExitSnapshot?.();
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography variant="caption">
                  Viewing snapshot from <strong>{new Date(sessionData.system_snapshot.timestamp).toLocaleString()}</strong>
                </Typography>
              </Alert>
            )}

            <Portal disablePortal={!isCanvasFullscreen}>
              <Paper
                elevation={3}
                sx={{
                  flexGrow: 1,
                  position: isCanvasFullscreen ? 'fixed' : 'relative',
                  inset: isCanvasFullscreen ? 0 : 'auto',
                  zIndex: isCanvasFullscreen ? 2147483000 : 'auto',
                  width: isCanvasFullscreen ? '100vw' : 'auto',
                  maxWidth: isCanvasFullscreen ? '100vw' : 'none',
                  minHeight: isCanvasFullscreen ? '100dvh' : 500,
                  height: isCanvasFullscreen ? '100dvh' : canUseInteractiveCanvas ? 680 : canvasNodes.length > 0 ? 600 : 500,
                  maxHeight: isCanvasFullscreen ? '100dvh' : 'none',
                  m: 0,
                  borderRadius: isCanvasFullscreen ? 0 : 2,
                  overflow: 'hidden',
                  bgcolor: isDark ? '#1a1a1a' : 'background.paper',
                  boxShadow: isCanvasFullscreen ? 24 : undefined,
                }}
              >
              {!canUseInteractiveCanvas && (
                <Box className="nodrag nopan" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 20 }}>
                  {fullscreenControl}
                </Box>
              )}

              {canUseInteractiveCanvas ? (
                <ReactFlowProvider>
                  <Box sx={{ width: '100%', height: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
                    <CanvasResizeFitView isFullscreen={isCanvasFullscreen} nodeCount={canvasNodes.length} />
                    <AISystemCanvas
                      nodes={canvasNodes}
                      edges={canvasEdges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      onNodeClick={onNodeClick!}
                      onAutoLayout={onAutoLayout!}
                      expertsData={canvasExpertsData}
                      addExpertToCanvas={addExpertToCanvas!}
                      setExpertFormOpen={setExpertFormOpen!}
                      selectedNode={selectedNode ?? null}
                      setSelectedNode={setSelectedNode!}
                      setNodes={setNodes!}
                      setEdges={setEdges!}
                      refetchExperts={refetchExperts!}
                      validationIssues={validationIssues}
                      onSaveCanvas={onSaveCanvas}
                      isSavingCanvas={isSavingCanvas}
                      onRunCanvas={onRunCanvas}
                      onStopCanvas={onStopCanvas}
                      isRunningCanvas={isRunningCanvas}
                      isStoppingCanvas={isStoppingCanvas}
                      canRunCanvas={canRunCanvas}
                      runDisabledReason={runDisabledReason}
                      expertRunStates={expertRunStates}
                      onViewExpertRun={onViewExpertRun}
                      toolbarActions={fullscreenControl}
                      isMobile={isMobile}
                      isDark={isDark}
                    />
                  </Box>
                </ReactFlowProvider>
              ) : experts.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <ReactFlowProvider>
                    <CanvasResizeFitView isFullscreen={isCanvasFullscreen} nodeCount={nodes.length} />
                    <ReactFlow
                      nodes={nodes.map((node) => ({
                        ...node,
                        data: { ...node.data, isDark },
                      }))}
                      edges={edges}
                      fitView
                      attributionPosition="bottom-left"
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={false}
                      nodesFocusable={false}
                      edgesFocusable={false}
                      panOnScroll={false}
                      zoomOnScroll
                      zoomOnPinch
                    >
                      <Background color={isDark ? '#444' : '#e0e0e0'} />
                      <Controls
                        showInteractive={false}
                        style={{
                          backgroundColor: isDark ? '#2a2a2a' : undefined,
                          borderColor: isDark ? '#555' : undefined,
                          boxShadow: isDark ? '0 0 4px rgba(0,0,0,0.5)' : undefined,
                        }}
                      />
                      {!isMobile && (
                        <MiniMap
                          nodeColor={(node: Node) => node.style?.background as string || (isDark ? '#444' : '#e0e0e0')}
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#f0f0f0' }}
                          maskColor={isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                        />
                      )}
                    </ReactFlow>
                  </ReactFlowProvider>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    p: { xs: 2, sm: 4 },
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary">
                    No Experts Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Create experts to visualize your AI system network
                  </Typography>
                </Box>
              )}
              </Paper>
            </Portal>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <SchedulePanel systemId={system.id} />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <AgentSchedulePanel systemId={system.id} />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <ScheduleExecutionsScreen aiSystemId={system.id} showStatsInitially={false} />
          </Box>
        </TabPanel>
      </Box>
    </Paper>
  );
};

export default AIBuilderInsightsPanel;
